import { ofType } from 'redux-observable';
import { from, of } from 'rxjs';
import { catchError, debounceTime, filter, mergeMap } from 'rxjs/operators';

import { OrderAction } from 'common/api/autogenerated/trading-gateway';

import { CRYPTO_MAX_FRACTION } from 'common/constants/fractions.constants';
import { localizeNumber } from 'common/helpers';
import { notify } from 'common/hooks/useNotification';
import { DerivativeOrderFullyExecutedPopup } from 'common/popups';
import { actionShowModal } from 'common/redux/modal/actions';
import { CommonEpic } from 'common/types/redux';

import { actionLoadingGetBalance } from 'views/wallet-new/redux/actions';

import ordersActionTypes from './orders.actionTypes';
import * as actions from './orders.actions';
import { actionGetCustomerOrders, actionGetCustomerTrades } from './orders.actions';
import * as ordersApi from './orders.api';

export const epicGetCustomerOrders: CommonEpic<typeof ordersApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(ordersActionTypes.LOADING_GET_CUSTOMER_ORDERS),
    debounceTime(700),
    mergeMap(() =>
      from(api.getOrders()).pipe(
        mergeMap((response) => of(actions.actionSuccessCustomerOrders(response), actions.actionGetLatestTrade())),
        catchError((error) => of(actions.actionErrorCustomerOrders(error))),
      ),
    ),
  );

export const epicGetCustomerTrades: CommonEpic<typeof ordersApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(ordersActionTypes.LOADING_GET_CUSTOMER_TRADES),
    debounceTime(700),
    mergeMap(() =>
      from(api.getTrades()).pipe(
        mergeMap((response) => of(actions.actionSuccessCustomerTrades(response))),
        catchError((error) => of(actions.actionErrorCustomerTrades(error))),
      ),
    ),
  );

export const epicGetLatestTrade: CommonEpic<typeof ordersApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(ordersActionTypes.LOADING_GET_LATEST_TRADE),
    debounceTime(700),
    mergeMap(() =>
      from(api.getTrades(1)).pipe(
        mergeMap((response) => of(actions.actionSuccessLatestTrade(response?.data?.[0] || null))),
        catchError((error) => of(actions.actionErrorLatestTrade(error))),
      ),
    ),
  );

export const epicDeleteOrder: CommonEpic<typeof ordersApi> = (action$, _state$, { api }) =>
  action$.pipe(
    ofType(ordersActionTypes.LOADING_DELETE_ORDER),
    mergeMap((action) =>
      from(api.deleteOrder(action.payload.orderId)).pipe(
        mergeMap((response) =>
          of(actionLoadingGetBalance(), actions.actionSuccessDeleteOrder(response), actions.actionGetCustomerOrders()),
        ),
        catchError((error) => of(actions.actionErrorDeleteOrder(error))),
      ),
    ),
  );

const showLimitPartiallyExecutedNotification = (
  amount: number | string,
  orderAction: string,
  baseCurrency: string,
  quoteCurrency: string,
  isBuy: boolean,
) =>
  notify({
    titleFn: (translate) =>
      translate('mobile:notifications:spot:order-executed:title')
        .replace('{PAIR}', `${baseCurrency}/${quoteCurrency}`)
        .replace('{DIRECTION}', orderAction.toUpperCase()),
    descriptionFn: (translate) =>
      amount
        ? `${translate(
            isBuy
              ? 'mobile:notifications:spot:order-executed:received'
              : 'mobile:notifications:spot:order-executed:spent',
          )}: ${amount} ${baseCurrency}`
        : '',
  });

export const epicReceiveSpotOrderPartiallyAndFullyExecuted: CommonEpic<typeof ordersApi> = (action$, _state$) =>
  action$.pipe(
    ofType(ordersActionTypes.SIGNALR_ORDER_UPDATED),
    filter((action) => action.payload.type === 'LIMIT'),
    debounceTime(500),
    mergeMap((action) => {
      const {
        orderId,
        amount: amountFromEvent = 0,
        cmd: orderAction = '',
        baseCurrency,
        quoteCurrency,
      } = action.payload;
      const { amount: currentAmount } =
        (_state$.value.ordersReducer.orders ?? []).find((order) => order.id === orderId) ?? {};

      const isBuy = orderAction === 'BUY';
      showLimitPartiallyExecutedNotification(
        currentAmount
          ? localizeNumber(currentAmount - amountFromEvent, {
              maxDigits: CRYPTO_MAX_FRACTION,
            })
          : '',
        orderAction,
        baseCurrency ?? '',
        quoteCurrency ?? '',
        isBuy,
      );
      return of(actions.actionSignalrOrderUpdatedSuccess(action.payload), actionGetCustomerTrades());
    }),
  );

export const epicReceiveSpotOrderFullyExecuted: CommonEpic<typeof ordersApi> = (action$, _state$) =>
  action$.pipe(
    ofType(ordersActionTypes.SIGNALR_ORDER_CLOSED),
    filter((action) => action.payload.type === 'LIMIT'),
    mergeMap((action) => {
      const {
        orderId,
        volume = 0,
        cmd: orderAction = '',
        baseCurrency,
        quoteCurrency,
        initialVolume = 0,
      } = action.payload;
      const isBuy = orderAction === 'BUY';

      if ((_state$.value.ordersReducer.orders ?? []).find((order) => order.id === orderId))
        showLimitPartiallyExecutedNotification(volume, orderAction, baseCurrency ?? '', quoteCurrency ?? '', isBuy);

      if (action.payload.orderId?.toString() === _state$.value.ordersReducer.orderIdIsEditing) {
        return of(
          actionGetCustomerOrders(),
          actionGetCustomerTrades(),
          actionShowModal(
            DerivativeOrderFullyExecutedPopup({
              baseCurrency: baseCurrency || '',
              quotedCurrency: quoteCurrency || '',
              action: orderAction === 'BUY' ? OrderAction.BUY : OrderAction.SELL,
              amount: initialVolume,
            }),
          ),
        );
      }

      return of(actionGetCustomerOrders(), actionGetCustomerTrades());
    }),
  );
